import { MarkerType } from "reactflow";

/**
 * Processes graph data to create Tag-Tag abstracted view
 * Removes intermediate ValueNodes and creates direct Tag-Tag edges
 *
 * Key Features:
 * - Edge labels show the actual value names that connect tags
 * - Edge thickness indicates connection strength
 * - Hover over edges to see connecting values
 * - Tags maintain full functionality (add values, edit, delete)
 * - Graph updates automatically when values are added/removed
 */
export const processTagTagData = (
  discoveryGraphData,
  hierarchyStats = null,
  layoutDirection = "vertical",
  selectedDiscoveryGraph = null,
) => {
  let nodeId = 0;
  const rootNodeId = `node_${nodeId++}`;

  // Layout constants - much shorter edges while preventing overlap of enhanced TagNodes
  // When layout is horizontal, we need more vertical space between nodes
  // When layout is vertical, we need more horizontal space between nodes
  // ULTRA COMPACT LAYOUT: Significantly tighter spacing to center elements
  const HORIZONTAL_SPACING = layoutDirection === "horizontal" ? 80 : 100; // Significantly reduced from 140/180
  const VERTICAL_SPACING = layoutDirection === "horizontal" ? 120 : 100; // Significantly reduced from 220/180
  const ROOT_Y_OFFSET = layoutDirection === "horizontal" ? 0 : -30; // Significantly reduced from 0/-100
  const ROOT_X_OFFSET = layoutDirection === "horizontal" ? -30 : 0; // Significantly reduced from -100/0

  const nodes = [
    {
      id: rootNodeId,
      type: "root",
      position: { x: ROOT_X_OFFSET, y: ROOT_Y_OFFSET },
      data: {
        label: "Root",
        nodePath: [],
      },
    },
  ];

  const edges = [];

  // DYNAMIC SPACING: Calculate based on number of nodes at each level
  const calculateDynamicSpacing = (nodeCount, level) => {
    // Reduce spacing for levels with fewer nodes
    const baseFactor = Math.min(1, nodeCount / 4); // Scale down if less than 4 nodes
    const depthFactor = Math.max(0.8, 1 - level * 0.05); // Reduce spacing at deeper levels (less aggressive)
    return Math.max(0.5, baseFactor * depthFactor); // Minimum 50% spacing
  };

  // Always return at least the root node, even if no other data exists
  if (!discoveryGraphData || Object.keys(discoveryGraphData).length === 0) {
    return { nodes, edges };
  }

  const tagConnections = new Map(); // Track connections between tags

  // Track positions and layout
  let minX = ROOT_X_OFFSET;
  let maxX = ROOT_X_OFFSET;
  let minY = ROOT_Y_OFFSET;
  let maxY = ROOT_Y_OFFSET;

  /**
   * Extract all tag-to-tag relationships from the graph
   */
  const extractTagConnections = (
    data,
    currentPath = [],
    parentTagId = null,
  ) => {
    if (!data || typeof data !== "object") return;

    const isQuestion = currentPath.length % 2 === 0;

    if (isQuestion) {
      // This is a tag level
      Object.entries(data).forEach(([tagName, tagData]) => {
        if (tagName === "TagAvailableValues") return;

        const tagPath = [...currentPath, tagName];
        const tagId = `tag_${nodeId++}`;

        // Store tag node info
        const tagInfo = {
          id: tagId,
          name: tagName,
          path: tagPath,
          parentTagId,
          connections: new Set(),
        };

        // Process values to find child tags
        if (tagData && typeof tagData === "object") {
          extractTagConnections(tagData, tagPath, tagId);
        }

        tagConnections.set(tagId, tagInfo);
      });
    } else {
      // This is a value level - look for child tags
      Object.entries(data).forEach(([valueName, valueData]) => {
        if (valueName === "TagAvailableValues") return;

        const valuePath = [...currentPath, valueName];

        // Process child tags under this value
        if (valueData && typeof valueData === "object") {
          extractTagConnections(valueData, valuePath, parentTagId);
        }
      });
    }
  };

  // Extract all connections
  extractTagConnections(discoveryGraphData);

  // Convert tag connections to positioned nodes
  const tagList = Array.from(tagConnections.values());
  const levelGroups = new Map(); // Group tags by their level (depth in hierarchy)

  // Group tags by level
  tagList.forEach((tag) => {
    const level = Math.floor(tag.path.length / 2);
    if (!levelGroups.has(level)) {
      levelGroups.set(level, []);
    }
    levelGroups.get(level).push(tag);
  });

  // Position tags by level
  Array.from(levelGroups.entries()).forEach(([level, tags]) => {
    // Apply dynamic spacing based on number of tags at this level
    const dynamicFactor = calculateDynamicSpacing(tags.length, level);
    const effectiveHorizontalSpacing = HORIZONTAL_SPACING * dynamicFactor;
    const effectiveVerticalSpacing = VERTICAL_SPACING * dynamicFactor;

    tags.forEach((tag, index) => {
      let xPos, yPos;

      if (layoutDirection === "horizontal") {
        xPos = level * HORIZONTAL_SPACING;
        yPos = (index - (tags.length - 1) / 2) * effectiveVerticalSpacing;
      } else {
        xPos = (index - (tags.length - 1) / 2) * effectiveHorizontalSpacing;
        yPos = level * VERTICAL_SPACING;
      }

      // Track bounds
      minX = Math.min(minX, xPos);
      maxX = Math.max(maxX, xPos);
      minY = Math.min(minY, yPos);
      maxY = Math.max(maxY, yPos);

      // Create node
      nodes.push({
        id: tag.id,
        type: "question", // Use TagNode type
        position: { x: xPos, y: yPos },
        data: {
          id: tag.id,
          label: tag.name,
          nodePath: tag.path,
          hideControls: false, // Show controls in tag-tag view
          nodeType: "tag", // Ensure proper node type
        },
      });

      // Create edge to parent tag (skip root level)
      if (tag.parentTagId && level > 0) {
        // Calculate shared values for edge weight
        const connectionInfo = calculateSharedValues(
          tag,
          tagConnections.get(tag.parentTagId),
          discoveryGraphData,
          hierarchyStats,
        );

        // Create label from actual value names and distributions
        let valueLabel = "connection";

        if (connectionInfo.isOpenEnded) {
          valueLabel = "open-ended";
        } else if (
          connectionInfo.valuesWithStats &&
          connectionInfo.valuesWithStats.length > 0
        ) {
          // Show values with file counts and both percentages
          const labelParts = connectionInfo.valuesWithStats.map(
            ({ value, fileCount, percentage, parentPercentage }) => {
              // Remove percentages from labels since they're shown separately in arrows
              return value;
            },
          );

          if (labelParts.length > 2) {
            valueLabel = `${labelParts.slice(0, 2).join(", ")} +${labelParts.length - 2} more`;
          } else {
            valueLabel = labelParts.join(", ");
          }
        } else {
          const validValues = connectionInfo.values.filter(
            (v) => v && v.trim() !== "",
          );
          if (validValues.length > 0) {
            valueLabel =
              validValues.length > 3
                ? `${validValues.slice(0, 3).join(", ")} +${validValues.length - 3} more`
                : validValues.join(", ");
          }
        }

        edges.push({
          id: `edge_${tag.parentTagId}_${tag.id}`,
          source: tag.parentTagId,
          target: tag.id,
          type: "smoothstep",
          sourceHandle: layoutDirection === "horizontal" ? "right" : "bottom",
          targetHandle: layoutDirection === "horizontal" ? "left" : "top",
          style: {
            strokeWidth: Math.max(
              1,
              Math.min(4, Math.log(connectionInfo.count + 1)),
            ), // Dynamic stroke width
            stroke: "#6B7280", // Darker gray for better visibility
            opacity: 1,
            strokeOpacity: 1,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 10,
            height: 10,
            color: "#6B7280", // Match edge color
          },
          data: {
            sharedValues: connectionInfo.count,
            connectingValues: connectionInfo.values,
            label: valueLabel,
            isOpenEnded: connectionInfo.isOpenEnded,
            valuesWithStats: connectionInfo.valuesWithStats || [],
            targetTagName: tag.name,
            sourceTagName: tagConnections.get(tag.parentTagId)?.name,
            sourceNodePath: tagConnections.get(tag.parentTagId)?.path,
            discoveryGraphData: discoveryGraphData,
            selectedDiscoveryGraph: selectedDiscoveryGraph,
          },
        });
      } else if (level === 0) {
        // Connect to root
        edges.push({
          id: `edge_${rootNodeId}_${tag.id}`,
          source: rootNodeId,
          target: tag.id,
          type: "smoothstep",
          sourceHandle: layoutDirection === "horizontal" ? "right" : "bottom",
          targetHandle: layoutDirection === "horizontal" ? "left" : "top",
          style: {
            strokeWidth: 2,
            stroke: "#6B7280", // Darker gray for better visibility
            opacity: 1,
            strokeOpacity: 1,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 10,
            height: 10,
            color: "#6B7280", // Match edge color
          },
        });
      }
    });
  });

  // Create bundle nodes and reorganize edges
  const { bundledNodes, bundledEdges } = createBundleNodes(
    nodes,
    edges,
    layoutDirection,
  );

  // Center the graph
  const allNodes = [...bundledNodes];
  const graphWidth =
    Math.max(...allNodes.map((n) => n.position.x)) -
    Math.min(...allNodes.map((n) => n.position.x));
  const graphHeight =
    Math.max(...allNodes.map((n) => n.position.y)) -
    Math.min(...allNodes.map((n) => n.position.y));
  const centerOffsetX =
    -graphWidth / 2 - Math.min(...allNodes.map((n) => n.position.x));
  const centerOffsetY =
    -graphHeight / 2 - Math.min(...allNodes.map((n) => n.position.y));

  allNodes.forEach((node) => {
    node.position.x += centerOffsetX;
    node.position.y += centerOffsetY;
  });

  return { nodes: bundledNodes, edges: bundledEdges };
};

/**
 * Calculate the shared values/connections between two tags and return value names
 */
const calculateSharedValues = (
  childTag,
  parentTag,
  graphData,
  hierarchyStats = null,
) => {
  if (!childTag || !parentTag || !graphData) {
    return { count: 1, values: [], isOpenEnded: false };
  }

  try {
    // Navigate to parent tag in graph data
    let parentData = graphData;
    for (let i = 0; i < parentTag.path.length; i++) {
      parentData = parentData[parentTag.path[i]];
      if (!parentData) return { count: 1, values: [], isOpenEnded: false };
    }

    // Check if parent tag is open-ended
    const values = Object.keys(parentData).filter(
      (key) => key !== "TagAvailableValues",
    );
    const isOpenEnded = values.includes("<ANY>");

    // If open-ended, don't show value names
    if (isOpenEnded) {
      return {
        count: 1,
        values: [],
        isOpenEnded: true,
      };
    }

    // Find values under parent that lead to child
    const connectingValues = [];
    const valuesWithStats = [];

    values.forEach((value) => {
      let valueData = parentData[value];
      if (valueData && typeof valueData === "object") {
        // Check if this value path leads to the child tag
        const childTagName = childTag.path[childTag.path.length - 1];
        if (valueData[childTagName]) {
          connectingValues.push(value);

          // Add distribution info if available
          if (hierarchyStats) {
            const valuePath = [...parentTag.path, value];
            const stats = findNodeStats(hierarchyStats, valuePath);

            // Also get parent stats to calculate relative percentage
            const parentStats = findNodeStats(hierarchyStats, parentTag.path);
            const parentFileCount = parentStats?.file_count || 0;

            valuesWithStats.push({
              value,
              fileCount: stats?.file_count || 0,
              percentage: stats?.percentage || 0, // Percentage of whole dataset
              parentPercentage:
                parentFileCount > 0
                  ? ((stats?.file_count || 0) / parentFileCount) * 100
                  : 0, // Percentage relative to parent
            });
          } else {
            valuesWithStats.push({
              value,
              fileCount: 0,
              percentage: 0,
              parentPercentage: 0,
            });
          }
        }
      }
    });

    return {
      count: Math.max(1, connectingValues.length),
      values: connectingValues,
      isOpenEnded: false,
      valuesWithStats,
    };
  } catch (error) {
    console.warn("Error calculating shared values:", error);
    return { count: 1, values: [], isOpenEnded: false };
  }
};

// Helper function to find node stats (same as in GraphNodeUtils)
const findNodeStats = (stats, nodePath) => {
  if (!stats || !nodePath) return null;

  let current = stats;

  for (const pathPart of nodePath) {
    if (!current) return null;

    // For root node
    if (pathPart === "root") {
      return current;
    }

    // Look for matching node in children
    if (current.children) {
      current = current.children.find((child) => child.name === pathPart);
    } else {
      return null;
    }
  }

  return current;
};

/**
 * Create intermediate bundle nodes for edges that share the same connecting values
 */
const createBundleNodes = (originalNodes, originalEdges, layoutDirection) => {
  // Group edges by their connecting values
  const edgeGroups = new Map();
  const rootEdges = []; // Keep root edges separate

  originalEdges.forEach((edge) => {
    if (!edge.data?.connectingValues) {
      rootEdges.push(edge); // These are root connections
      return;
    }

    // Create a key from the sorted connecting values
    const valueKey = edge.data.connectingValues.slice().sort().join(",");

    if (!edgeGroups.has(valueKey)) {
      edgeGroups.set(valueKey, []);
    }
    edgeGroups.get(valueKey).push(edge);
  });

  const bundleNodes = [...originalNodes]; // Start with original nodes
  const bundleEdges = [...rootEdges]; // Start with root edges
  let bundleNodeId = 1000; // Start bundle node IDs from 1000 to avoid conflicts

  // Process each group
  edgeGroups.forEach((edgesInGroup, valueKey) => {
    if (edgesInGroup.length === 1) {
      // Single edge - no bundling needed
      bundleEdges.push(edgesInGroup[0]);
    } else {
      // Multiple edges with same values - create bundle node
      const firstEdge = edgesInGroup[0];
      const sourceNode = originalNodes.find((n) => n.id === firstEdge.source);
      const targetNodes = edgesInGroup.map((edge) =>
        originalNodes.find((n) => n.id === edge.target),
      );

      // Calculate bundle node position (midpoint between source and average of targets)
      const avgTargetX =
        targetNodes.reduce((sum, node) => sum + node.position.x, 0) /
        targetNodes.length;
      const avgTargetY =
        targetNodes.reduce((sum, node) => sum + node.position.y, 0) /
        targetNodes.length;

      // Position bundle node closer to center with reduced spacing
      const bundleX =
        sourceNode.position.x + (avgTargetX - sourceNode.position.x) * 0.4;
      const bundleY =
        sourceNode.position.y + (avgTargetY - sourceNode.position.y) * 0.4;

      // Create bundle node
      const bundleNodeIdStr = `bundle_${bundleNodeId++}`;
      const valueLabels = firstEdge.data.connectingValues;
      const valueWithStats = firstEdge.data.valuesWithStats || [];

      // Create label with file counts and percentages
      let bundleLabel;
      if (valueWithStats.length > 0) {
        const labelParts = valueWithStats.map(
          ({ value, fileCount, percentage, parentPercentage }) => {
            // Remove percentages from labels since they're shown separately in arrows
            return value;
          },
        );
        bundleLabel = labelParts.join(", ");
      } else {
        bundleLabel = valueLabels.join(", ");
      }

      bundleNodes.push({
        id: bundleNodeIdStr,
        type: "bundle", // We'll need to create this node type
        position: { x: bundleX, y: bundleY },
        data: {
          label: bundleLabel,
          connectingValues: valueLabels,
          targetCount: edgesInGroup.length,
          valuesWithStats: valueWithStats,
          sourceTagName: firstEdge.data.sourceTagName, // Add source tag name for data slice creation
          sourceNodePath: firstEdge.data.sourceNodePath, // Add source node path for complete hierarchy
        },
      });

      // Create edge from source to bundle node - preserve original data for data slice creation
      bundleEdges.push({
        id: `source_to_bundle_${bundleNodeIdStr}`,
        source: firstEdge.source,
        target: bundleNodeIdStr,
        type: "smoothstep",
        sourceHandle: layoutDirection === "horizontal" ? "right" : "bottom",
        targetHandle: layoutDirection === "horizontal" ? "left" : "top",
        style: {
          strokeWidth: Math.max(
            2,
            Math.min(4, Math.log(edgesInGroup.length + 2)),
          ),
          stroke: "#6B7280", // Darker gray for better visibility
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 10,
          height: 10,
          color: "#6B7280", // Match edge color
        },
        data: {
          // Preserve original edge data for data slice creation
          ...firstEdge.data,
          label: bundleLabel,
          isBundleInput: true,
          bundledEdgesData: edgesInGroup.map((edge) => edge.data), // Store all original edge data
          sourceNodePath: firstEdge.data.sourceNodePath, // Ensure sourceNodePath is preserved
        },
      });

      // Create edges from bundle node to each target
      edgesInGroup.forEach((originalEdge, index) => {
        bundleEdges.push({
          id: `bundle_to_target_${bundleNodeIdStr}_${originalEdge.target}`,
          source: bundleNodeIdStr,
          target: originalEdge.target,
          type: "smoothstep",
          sourceHandle: layoutDirection === "horizontal" ? "right" : "bottom",
          targetHandle: layoutDirection === "horizontal" ? "left" : "top",
          style: {
            strokeWidth: 2,
            stroke: "#6B7280", // Darker gray for better visibility
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 8,
            height: 8,
            color: "#6B7280", // Match edge color
          },
          data: {
            label: "",
            isBundleOutput: true,
            targetTagName: originalEdge.data.targetTagName,
            // Pass through file count info for hover highlighting
            valuesWithStats: originalEdge.data.valuesWithStats,
          },
        });
      });
    }
  });

  return { bundledNodes: bundleNodes, bundledEdges: bundleEdges };
};

/**
 * Get edge style based on connection strength
 */
export const getEdgeStyle = (connectionInfo) => {
  const sharedValues =
    typeof connectionInfo === "object" ? connectionInfo.count : connectionInfo;
  const baseStyle = {
    stroke: "#6B7280", // Base dark gray
    strokeDasharray: "none",
    strokeOpacity: 1,
  };

  if (sharedValues > 10) {
    return { ...baseStyle, strokeWidth: 4, stroke: "#374151" }; // Even darker gray
  } else if (sharedValues > 5) {
    return { ...baseStyle, strokeWidth: 3, stroke: "#4B5563" }; // Dark gray
  } else if (sharedValues > 2) {
    return { ...baseStyle, strokeWidth: 2, stroke: "#6B7280" }; // Medium gray
  } else {
    return { ...baseStyle, strokeWidth: 1, stroke: "#6B7280" }; // Same medium gray for clarity
  }
};
